/**
 * Offline mutation queue — queues API mutations when offline and replays on reconnect.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';

export interface QueuedMutation {
  id: string;
  /** Account that created the mutation. Missing only on pre-migration entries. */
  ownerId?: string;
  method: 'post' | 'put' | 'delete';
  url: string;
  data?: unknown;
  createdAt: string;
  retries: number;
}

interface OfflineQueueState {
  queue: QueuedMutation[];
  processing: boolean;
  enqueue: (ownerId: string, method: QueuedMutation['method'], url: string, data?: unknown) => void;
  dequeue: (id: string) => void;
  clear: () => void;
  clearForOwner: (ownerId: string) => void;
  setProcessing: (v: boolean) => void;
  flush: (
    ownerId: string,
    executor: (m: QueuedMutation) => Promise<void>,
  ) => Promise<{ ok: number; failed: number }>;
}

export const useOfflineQueue = create<OfflineQueueState>()(
  persist(
    (set, get) => ({
      queue: [],
      processing: false,

      enqueue(ownerId, method, url, data) {
        const mutation: QueuedMutation = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          ownerId,
          method,
          url,
          data,
          createdAt: new Date().toISOString(),
          retries: 0,
        };
        set(s => ({ queue: [...s.queue, mutation].slice(-50) })); // max 50
      },

      dequeue(id) {
        set(s => ({ queue: s.queue.filter(m => m.id !== id) }));
      },

      clear() {
        set({ queue: [] });
      },

      clearForOwner(ownerId) {
        set(s => ({ queue: s.queue.filter(m => m.ownerId !== ownerId) }));
      },

      setProcessing(v) {
        set({ processing: v });
      },

      async flush(ownerId, executor) {
        const { queue, processing } = get();
        if (processing || queue.length === 0 || !ownerId) return { ok: 0, failed: 0 };

        // Legacy entries were persisted before mutations were account-scoped.
        // They cannot be replayed safely because reminder sequence numbers are
        // per user, so discard them instead of guessing an owner.
        const legacyIds = new Set(queue.filter(m => !m.ownerId).map(m => m.id));
        const ownedQueue = queue.filter(m => m.ownerId === ownerId);
        if (ownedQueue.length === 0) {
          if (legacyIds.size > 0) {
            set(state => ({ queue: state.queue.filter(m => !legacyIds.has(m.id)) }));
          }
          return { ok: 0, failed: 0 };
        }

        set({ processing: true });
        let ok = 0;
        let failed = 0;
        const remaining: QueuedMutation[] = [];

        for (const m of ownedQueue) {
          try {
            await executor(m);
            ok++;
          } catch {
            // Keep the mutation durable across longer outages. The queue cap
            // still bounds storage, while a transient fourth failure no
            // longer discards a user's Done action silently.
            remaining.push({ ...m, retries: m.retries + 1 });
            failed++;
          }
        }

        const processedIds = new Set(ownedQueue.map(m => m.id));
        set(state => {
          const currentIds = new Set(state.queue.map(m => m.id));
          return {
            // Preserve mutations enqueued while this flush was in progress,
            // while respecting an explicit dequeue/account clear during it.
            queue: [
              ...remaining.filter(m => currentIds.has(m.id)),
              ...state.queue.filter(m => !processedIds.has(m.id) && !legacyIds.has(m.id)),
            ].slice(-50),
            processing: false,
          };
        });
        return { ok, failed };
      },
    }),
    {
      name: 'offline-mutation-queue',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (s) => ({ queue: s.queue }),
    }
  )
);
